import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Alert,
  Typography,
  Chip,
  Snackbar,
  IconButton,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';

import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import SummaryCard from '../SummaryCard';
import CancelsSummaryCard from '../CancelsSummaryCard';
import ResultsTable from '../ResultsTable';
import ProductSelectionsTable from '../ProductSelectionsTable';
import CustomLegend from '../CustomLegend';
import SelectionSummaryCard from '../SelectionSummaryCard';
import StatusChangesTab from '../StatusChangesTab';
import AnimatedChart from '../AnimatedChart';
import GestureWrapper from '../GestureWrapper';
import PullToRefresh from '../PullToRefresh';
import usePWA from '../../hooks/usePWA';

import GlobalSearchResults from './GlobalSearchResults';
import {
  ALL_PROGRAMS_OPTION,
  PROGRAM_OPTIONS,
  TAB_ACTION_REQUIRED,
  TAB_BULK_SEND,
  TAB_CANCELS,
  TAB_ENROLLMENT_STATUS_CHANGES,
  TAB_ENROLLMENT,
  TAB_HISTORICAL_TRENDS,
  TAB_EMAILS_SENT,
  TAB_SELECTIONS,
  TAB_SEND_HISTORY,
  TAB_SINGLE_API_SEARCH,
  TAB_STATUS_CHANGES,
  TAB_VMT_STAGING,
  TabFallback,
} from './constants';

import { PROGRAMS } from '../../constants/programs';
import { sanitizeBacSearchValue } from './utils';

const LazyHistoricalTrends = React.lazy(() => import('../HistoricalTrends'));
const LazySingleBacSearch = React.lazy(() => import('../SingleBacSearch'));
const LazySendHistory = React.lazy(() => import('../SendHistoryMRT'));
const LazyBulkSend = React.lazy(() => import('../BulkSend'));
const LazyActionRequiredChart = React.lazy(() => import('../ActionRequiredChart'));
const LazyActionRequiredTab = React.lazy(() => import('../ActionRequiredTab'));
const LazyEmailsSent = React.lazy(() => import('../EmailsSentReport'));
const LazyCancelsReport = React.lazy(() => import('../CancelsReport'));
const LazyEnrollmentStatusChanges = React.lazy(() => import('../EnrollmentStatusChangesTab'));

const DashboardView = ({
  user,
  hasSearched,
  navigateToDealer,
  handleOpenDetail,
  detailDrawer,
  maximizedView,
  toggleResultsMaximize,
  toggleSelectionsMaximize,
  toggleStatusChangesMaximize,
  filteredData,
  filteredSelectionRows,
  referenceFilteredSelectionRows,
  statusMatchChartData,
  handleStatusMatchFilterChange,
  currentTab,
  setCurrentTab,
  isCompactDashboard,
  isSmallScreen,
  expandedSections,
  handleSectionToggle,
  gestureHintOpen,
  handleDismissGestureHint,
  handlePullToRefresh,
  isFetching,
  activeFilter,
  clearActiveFilter,
  handleStatusChartClick,
  handleSelectionChartClick,
  handleCancelsChartClick,
  searchType,
  setSearchType,
  searchValue,
  setSearchValue,
  snapshotDate,
  setSnapshotDate,
  isSnapshotLoading,
  snapshotContext,
  snapshotErrors,
  snapshotFailures,
  handleSnapshotLoad,
  handleSnapshotClear,
  isWithoutSelectionsFilter,
  handleRowUpdated,
  tableSnackbar,
  handleCloseTableSnackbar,
}) => {
  const { isStandalone, isMobile } = usePWA();
  const renderStatusMatchCard = (cardWidth) => (
    <Card sx={{ minWidth: cardWidth, flex: '0 0 auto' }}>
      <CardHeader
        title="Status Match Overview"
        subheader="Selection coverage (status match)"
        action={(
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            label={`${statusMatchChartData[0]?.value + statusMatchChartData[1]?.value || 0} compared`}
          />
        )}
      />
      <CardContent>
        {referenceFilteredSelectionRows.length > 0 ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
              <AnimatedChart
                animationKey={`status-match:${statusMatchChartData.map((d) => d.value).join('-')}`}
              >
                <PieChart
                  series={[{ data: statusMatchChartData, innerRadius: 30 }]}
                  width={150}
                  height={150}
                  legend={{ hidden: true }}
                  margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                  onClick={(_event, item) => {
                    const label = statusMatchChartData[item?.dataIndex]?.label;
                    if (label) handleStatusMatchFilterChange(label);
                  }}
                />
              </AnimatedChart>
            </Box>
            <CustomLegend data={statusMatchChartData} onLegendClick={handleStatusMatchFilterChange} />
          </>
        ) : (
          <Alert severity="info">No product selections available for status match analysis.</Alert>
        )}
      </CardContent>
    </Card>
  );

  const refreshProgramName =
    searchType === 'program' && searchValue && searchValue !== ALL_PROGRAMS_OPTION ? searchValue : null;

  if (hasSearched) {
    return (
      <>
        <GlobalSearchResults
          onNavigateToDealer={navigateToDealer}
          onOpenDetail={handleOpenDetail}
          onToggleMaximize={toggleResultsMaximize}
          isMaximized={maximizedView === 'results'}
          userEmail={user?.email || null}
        />
        {detailDrawer}
      </>
    );
  }

  if (maximizedView === 'results') {
    return (
      <>
        <Box sx={{ mt: 4, height: 'calc(100vh - 120px)' }}>
          <ResultsTable
            data={filteredData}
            isMaximized
            onToggleMaximize={toggleResultsMaximize}
            onNavigateToDealer={navigateToDealer}
            onRowUpdated={handleRowUpdated}
            onOpenDetail={handleOpenDetail}
          />
        </Box>
        {detailDrawer}
      </>
    );
  }

  if (maximizedView === 'selections') {
    return (
      <>
        <Box sx={{ mt: 4, height: 'calc(100vh - 120px)' }}>
          <ProductSelectionsTable
            rows={filteredSelectionRows}
            isMaximized
            onToggleMaximize={toggleSelectionsMaximize}
            onNavigateToDealer={navigateToDealer}
            userEmail={user?.email || null}
            onOpenDetail={handleOpenDetail}
            refreshProgramName={refreshProgramName}
          />
        </Box>
        {detailDrawer}
      </>
    );
  }

  if (maximizedView === 'statusChanges') {
    return (
      <>
        <Box sx={{ mt: 4, height: 'calc(100vh - 120px)' }}>
          <StatusChangesTab
            isMaximized
            onToggleMaximize={toggleStatusChangesMaximize}
            onNavigateToDealer={navigateToDealer}
            userEmail={user?.email || null}
            onOpenDetail={handleOpenDetail}
            enableInternalScroll
          />
        </Box>
        {detailDrawer}
      </>
    );
  }

  return (
    <>
      <GestureWrapper
        enabled={isCompactDashboard && !isStandalone && !isMobile}
        onSwipeLeft={() => setCurrentTab((prev) => Math.min(prev + 1, TAB_BULK_SEND))}
        onSwipeRight={() => setCurrentTab((prev) => Math.max(prev - 1, 0))}
        // @use-gesture recommends avoiding touch-action: auto on drag targets.
        // pan-y preserves vertical scrolling while allowing horizontal swipe gestures.
        sx={{ touchAction: 'pan-y' }}
      >
        {isCompactDashboard && (
          <PullToRefresh enabled onRefresh={handlePullToRefresh} />
        )}
        {isCompactDashboard && gestureHintOpen && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <IconButton aria-label="dismiss gesture tip" size="small" onClick={handleDismissGestureHint}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            Tip: swipe left/right anywhere outside the table to switch tabs.
          </Alert>
        )}

        {currentTab === TAB_ENROLLMENT && (
          <>
            {isCompactDashboard ? (
              <Accordion
                expanded={expandedSections.enrollmentStatus}
                onChange={handleSectionToggle('enrollmentStatus')}
                sx={{ mb: 2 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Program Status Overview
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {PROGRAMS.map((program) => (
                      <Grid item xs={12} key={program}>
                        <SummaryCard programName={program} onChartClick={handleStatusChartClick} />
                      </Grid>
                    ))}
                    <Grid item xs={12}>
                      <CancelsSummaryCard onProgramSelect={handleCancelsChartClick} />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ) : (
              <Box sx={{ mb: 2 }}>
                <Box
                  data-testid="enrollment-status-row"
                  sx={{
                    display: 'flex',
                    gap: 2,
                    overflowX: 'auto',
                    pb: 1,
                    scrollSnapType: 'x mandatory',
                    '& > *': {
                      scrollSnapAlign: 'start',
                    },
                  }}
                >
                  {PROGRAMS.map((program) => (
                    <Box key={program} sx={{ minWidth: 300, flex: '0 0 auto' }}>
                      <SummaryCard programName={program} onChartClick={handleStatusChartClick} />
                    </Box>
                  ))}
                  <Box sx={{ minWidth: 300, flex: '0 0 auto' }}>
                    <CancelsSummaryCard onProgramSelect={handleCancelsChartClick} />
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}

        {currentTab === TAB_SELECTIONS && (
          <>
            {isCompactDashboard ? (
              <Accordion
                expanded={expandedSections.selectionsSummary}
                onChange={handleSectionToggle('selectionsSummary')}
                sx={{ mb: 2 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Selections Overview
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      overflowX: 'auto',
                      pb: 1,
                      scrollSnapType: 'x mandatory',
                      '& > *': {
                        scrollSnapAlign: 'start',
                      },
                    }}
                  >
                    {PROGRAMS.map((program) => (
                      <Box key={program} sx={{ minWidth: isSmallScreen ? 260 : 320, flex: '0 0 auto' }}>
                        <SelectionSummaryCard programName={program} onChartClick={handleSelectionChartClick} />
                      </Box>
                    ))}
                    {renderStatusMatchCard(isSmallScreen ? 300 : 320)}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ) : (
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    overflowX: 'auto',
                    pb: 1,
                    scrollSnapType: 'x mandatory',
                    '& > *': {
                      scrollSnapAlign: 'start',
                    },
                  }}
                >
                  {PROGRAMS.map((program) => (
                    <Box key={program} sx={{ minWidth: 300, flex: '0 0 auto' }}>
                      <SelectionSummaryCard programName={program} onChartClick={handleSelectionChartClick} />
                    </Box>
                  ))}
                  {renderStatusMatchCard(320)}
                </Box>
              </Box>
            )}
          </>
        )}

        {currentTab === TAB_STATUS_CHANGES && (
          <StatusChangesTab
            isMaximized={maximizedView === 'statusChanges'}
            onToggleMaximize={toggleStatusChangesMaximize}
            onNavigateToDealer={navigateToDealer}
            userEmail={user?.email || null}
            onOpenDetail={handleOpenDetail}
            enableInternalScroll
          />
        )}
        {currentTab === TAB_ENROLLMENT_STATUS_CHANGES && (
          <React.Suspense fallback={<TabFallback />}>
            <LazyEnrollmentStatusChanges />
          </React.Suspense>
        )}

        {currentTab === TAB_HISTORICAL_TRENDS && (
          <React.Suspense fallback={<TabFallback />}>
            <LazyHistoricalTrends />
          </React.Suspense>
        )}

        {currentTab === TAB_SINGLE_API_SEARCH && (
          <React.Suspense fallback={<TabFallback />}>
            <LazySingleBacSearch
              onOpenDetail={handleOpenDetail}
              onNavigateToDealer={navigateToDealer}
            />
          </React.Suspense>
        )}

        {currentTab === TAB_SEND_HISTORY && (
          <React.Suspense fallback={<TabFallback />}>
            <LazySendHistory />
          </React.Suspense>
        )}
        {currentTab === TAB_EMAILS_SENT && (
          <React.Suspense fallback={<TabFallback />}>
            <LazyEmailsSent />
          </React.Suspense>
        )}
        {currentTab === TAB_VMT_STAGING && (
          <React.Suspense fallback={<TabFallback />}>
            <LazyBulkSend showUploadSection={false} />
          </React.Suspense>
        )}
        {currentTab === TAB_BULK_SEND && (
          <React.Suspense fallback={<TabFallback />}>
            <LazyBulkSend showStagingSection={false} />
          </React.Suspense>
        )}
        {currentTab === TAB_ACTION_REQUIRED && (
          <React.Suspense fallback={<TabFallback />}>
            <LazyActionRequiredChart />
            <LazyActionRequiredTab onNavigateToDealer={navigateToDealer} onOpenDetail={handleOpenDetail} />
          </React.Suspense>
        )}
        {currentTab === TAB_CANCELS && (
          <React.Suspense fallback={<TabFallback />}>
            <LazyCancelsReport />
          </React.Suspense>
        )}
      </GestureWrapper>
      {currentTab === TAB_ENROLLMENT && (
        <>
          <Card sx={{ mt: 2 }}>
            <CardHeader title="Search Enrollments" />
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ m: 1, minWidth: 240 }}>
                  <InputLabel>Search by</InputLabel>
                  <Select
                    value={searchType}
                    label="Search by"
                    onChange={(event) => {
                      const nextType = event.target.value;
                      setSearchType(nextType);
                      setSearchValue(nextType === 'program' ? PROGRAM_OPTIONS[0] : '');
                    }}
                  >
                    <MenuItem value="program">Program</MenuItem>
                    <MenuItem value="bac">BAC</MenuItem>
                  </Select>
                </FormControl>
                {searchType === 'program' ? (
                  <FormControl sx={{ m: 1, minWidth: 240, flex: 1 }}>
                    <InputLabel>Program</InputLabel>
                    <Select value={searchValue} label="Program" onChange={(event) => setSearchValue(event.target.value)}>
                      {PROGRAM_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    label="BAC Code"
                    variant="outlined"
                    value={searchValue}
                    onChange={(event) => setSearchValue(sanitizeBacSearchValue(event.target.value))}
                    sx={{ m: 1, minWidth: 240, flex: 1 }}
                    type="text"
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
                  />
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <TextField
                    label="Snapshot Date"
                    type="date"
                    value={snapshotDate}
                    onChange={(event) => setSnapshotDate(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ minWidth: 170 }}
                  />
                  <Button variant="contained" onClick={handleSnapshotLoad} disabled={isSnapshotLoading}>
                    {isSnapshotLoading ? 'Loading...' : 'Load Snapshot'}
                  </Button>
                  <Button variant="outlined" onClick={handleSnapshotClear} disabled={!snapshotContext && !snapshotDate}>
                    Clear Snapshot
                  </Button>
                </Box>
              </Box>
              {snapshotContext && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="info">
                    Showing the exact daily snapshot for {snapshotContext.requested}.
                    {snapshotContext.captures?.length > 0 && (
                      <>
                        {' '}Source feed times:
                        {snapshotContext.captures
                          .filter((item) => item.capturedAt)
                          .map((item) => `${item.programName}: ${new Date(item.capturedAt).toLocaleString()}`)
                          .join('; ')}
                      </>
                    )}
                  </Alert>
                </Box>
              )}
              {snapshotErrors.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="warning">
                    No snapshot was available for {snapshotErrors.join(', ')} on {snapshotDate}. Try a different date.
                  </Alert>
                </Box>
              )}
              {snapshotFailures.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="error">
                    Historical snapshot failed for {snapshotFailures.map((failure) => failure.programName).join(', ')}.
                    {' '}{snapshotFailures[0].message}
                  </Alert>
                </Box>
              )}
            </CardContent>
          </Card>

          <Box sx={{ mt: 4 }}>
            {isFetching && <CircularProgress />}
            {activeFilter && (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">Filtered Report:</Typography>
                <Chip label={`${activeFilter.programName} - ${activeFilter.value}`} onDelete={clearActiveFilter} />
              </Box>
            )}
            {filteredData.length > 0 && !isFetching && (
              <ResultsTable
                data={filteredData}
                isMaximized={maximizedView === 'results'}
                onToggleMaximize={toggleResultsMaximize}
                onNavigateToDealer={navigateToDealer}
                onRowUpdated={handleRowUpdated}
                snapshotContext={snapshotContext}
                onOpenDetail={handleOpenDetail}
              />
            )}
            {filteredData.length === 0 && !isFetching && (
              <Alert severity="info">No enrollment records match the current filters.</Alert>
            )}
          </Box>
        </>
      )}

      {currentTab === TAB_SELECTIONS && (
        <>
          <Card sx={{ mt: 2 }}>
            <CardHeader title="Search Product Selections" />
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ m: 1, minWidth: 240 }}>
                  <InputLabel>Search by</InputLabel>
                  <Select
                    value={searchType}
                    label="Search by"
                    onChange={(event) => {
                      const nextType = event.target.value;
                      setSearchType(nextType);
                      setSearchValue(nextType === 'program' ? PROGRAM_OPTIONS[0] : '');
                    }}
                  >
                    <MenuItem value="program">Program</MenuItem>
                    <MenuItem value="bac">BAC</MenuItem>
                  </Select>
                </FormControl>
                {searchType === 'program' ? (
                  <FormControl sx={{ m: 1, minWidth: 240, flex: 1 }}>
                    <InputLabel>Program</InputLabel>
                    <Select value={searchValue} label="Program" onChange={(event) => setSearchValue(event.target.value)}>
                      {PROGRAM_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    label="BAC Code"
                    variant="outlined"
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                    sx={{ m: 1, minWidth: 240, flex: 1 }}
                    type="number"
                  />
                )}
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ mt: 4 }}>
            {isFetching && <CircularProgress />}
            {activeFilter && (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">Filtered Report:</Typography>
                <Chip label={`${activeFilter.programName} - ${activeFilter.value}`} onDelete={clearActiveFilter} />
              </Box>
            )}
            {!isFetching && !isWithoutSelectionsFilter && (
              filteredSelectionRows.length > 0 ? (
                <ProductSelectionsTable
                  rows={filteredSelectionRows}
                  isMaximized={maximizedView === 'selections'}
                  onToggleMaximize={toggleSelectionsMaximize}
                  onNavigateToDealer={navigateToDealer}
                  userEmail={user?.email || null}
                  onOpenDetail={handleOpenDetail}
                  enableInternalScroll
                  refreshProgramName={refreshProgramName}
                />
              ) : (
                <Alert severity="info">No product selections match the current filters.</Alert>
              )
            )}
            {!isFetching && isWithoutSelectionsFilter && (
              filteredData.length > 0 ? (
                <ResultsTable
                  data={filteredData}
                  isMaximized={maximizedView === 'selections'}
                  onToggleMaximize={toggleSelectionsMaximize}
                  onNavigateToDealer={navigateToDealer}
                  onRowUpdated={handleRowUpdated}
                  snapshotContext={snapshotContext}
                  onOpenDetail={handleOpenDetail}
                />
              ) : (
                <Alert severity="info">No dealers without selections match the current filters.</Alert>
              )
            )}
          </Box>
        </>
      )}

      <Snackbar
        open={tableSnackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseTableSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseTableSnackbar}
          severity={tableSnackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {tableSnackbar.message}
        </Alert>
      </Snackbar>
      {detailDrawer}
    </>
  );
};

export default DashboardView;
